// opencode, as the panels need it: the sessions on the Mac, one session's transcript kept live from the event
// stream, and a prompt going the other way. The same reading of opencode's shapes as the headset's
// OpencodeAdapter.swift, cut down to what the WebXR client draws today. Pure data in, callbacks out; the
// transport is whatever `transport.js` gave us.
import { displayTitle } from './session-title.js'
import { readReason, plan, recordError } from './server-errors.js'

export class Client {
  constructor(transport) {
    this.transport = transport
    this.sessions = []          // [{id, title, directory, updatedAt}]
    this.session = null         // the open one: {id, title, directory}
    this.messages = new Map()   // messageID → {id, role, parts: Map(partID → part), order: [partID]}
    this.order = []             // messageIDs in time order
    this.status = 'idle'        // idle | busy | retry
    this.error = null
    this.onChange = () => {}
    this.streaming = false
    // the rest of what the panels draw: the open session's todos and diff, the models the Mac offers
    this.todos = []             // [{text, done, status}]
    this.diff = []              // [{file, additions, deletions, rows: [[kind, num, text]]}]
    this.providers = []         // [{id, name, models: [{id, name, free}]}]
    this.defaultModel = null    // 'provider/model'
    this.refused = {}           // 'sessions' | 'models' → {status, reason, failures} while the Mac refuses that list (server-errors.js)
    this.retryTimer = null
    this.timers = { set: (f, ms) => setTimeout(f, ms), clear: (t) => clearTimeout(t) }
    this.diffTimer = null
    // every session open in a window keeps its own transcript and state, and all of them stay live from the one
    // event stream — several session windows side by side, as on the headset (Josh, 09-27)
    this.views = new Map()      // sessionID → {session, messages, order, status, error, todos}
    this.statuses = new Map()   // sessionID → 'busy' | 'retry' | 'idle', for every session (Home's dots)
    this.listeners = new Set()  // windows that want opencode's raw events (permissions, questions, terminals)
  }

  /// Every raw opencode event, as it arrives: `fn({type, properties})`. Returns the unsubscribe.
  onEvent(fn) { this.listeners.add(fn); return () => this.listeners.delete(fn) }

  // MARK: session views — one per window

  view(id) {
    let v = this.views.get(id)
    if (!v) { v = { session: this.sessions.find((x) => x.id === id) || { id, title: '', directory: '' }, messages: new Map(), order: [], status: 'idle', error: null, todos: [], sources: this.sources?.[id] || [] }; this.views.set(id, v) }
    return v
  }

  /// The history merged into what is shown, never cleared first (Josh 10-09: reconnects blanked it): by message id, the
  /// Mac's parts replace held ones, an event's message from during the fetch stays; a failed fetch changes nothing.
  async openView(id) {
    const v = this.view(id)
    v.session = this.sessions.find((x) => x.id === id) || v.session
    const before = new Set(v.order)
    const raw = await this.transport.request('GET', `session/${id}/message`, { query: { directory: v.session.directory } })
    const fresh = []
    for (const item of raw || []) {
      const info = item.info || item
      if (!info.id || fresh.includes(info.id)) continue
      this.upsertInto(v, info)
      fresh.push(info.id)
      if (!Array.isArray(item.parts)) continue
      const m = v.messages.get(info.id), ids = new Set(item.parts.map((p) => p.id))
      for (const pid of m.order) if (!ids.has(pid)) m.parts.delete(pid)
      m.order = m.order.filter((pid) => ids.has(pid))
      for (const p of item.parts) this.upsertPartInto(v, p)
    }
    const seen = new Set(fresh)
    for (const mid of before) if (!seen.has(mid)) v.messages.delete(mid)
    v.order = [...fresh, ...v.order.filter((mid) => !seen.has(mid) && !before.has(mid))]
    this.onChange('transcript', id)
    this.refreshViewTodos(id).catch(() => {})
    return v
  }

  /// A new connection's client takes the last one's views and lists (nothing blanks); `seed` adds a window's kept copy.
  adopt(prev) {
    for (const [id, v] of prev?.views || []) if (!this.views.has(id)) this.views.set(id, v)
    for (const [id, st] of prev?.statuses || []) if (!this.statuses.has(id)) this.statuses.set(id, st)
    if (!this.sessions.length && prev?.sessions?.length) this.sessions = prev.sessions
    if (!this.providers.length && prev?.providers?.length) { this.providers = prev.providers; this.defaultModel = prev.defaultModel }
    return this
  }

  seed(id, held) {
    const v = this.views.get(id)
    if (!held?.order?.length || v?.order.length) return
    this.views.set(id, { session: v?.session || held.session, messages: new Map([...held.messages].map(([k, m]) => [k, { ...m, parts: new Map(m.parts), order: [...m.order] }])), order: [...held.order], status: v?.status || 'idle', error: null, todos: held.todos || [] })
  }

  refreshViews() { return Promise.allSettled([...this.views.keys()].map((id) => this.openView(id))) }

  closeView(id) { this.views.delete(id) }

  async refreshViewTodos(id) {
    const v = this.views.get(id); if (!v) return
    const raw = await this.transport.request('GET', `session/${id}/todo`, { query: { directory: v.session.directory } })
    v.todos = (Array.isArray(raw) ? raw : []).map((t) => ({ text: t.content || '', status: t.status || 'pending', done: t.status === 'completed' }))
    this.onChange('todos', id)
  }

  transcriptOf(id) {
    const v = this.views.get(id); if (!v) return []
    const out = []
    for (const mid of v.order) {
      const m = v.messages.get(mid); if (!m) continue
      for (const pid of m.order) {
        const p = m.parts.get(pid); if (!p) continue
        if (p.kind === 'text' && p.text) out.push({ who: m.role === 'user' ? 'you' : 'cody', text: p.text })
        else if (p.kind === 'tool') out.push({ who: 'tool', text: `${p.tool}${p.title ? ' · ' + p.title : ''}${p.state === 'running' || p.state === 'pending' ? ' …' : ''}` })
        else if (p.kind === 'reasoning' && p.text) out.push({ who: 'thinking', text: p.text })
      }
      if (m.error) out.push({ who: 'tool', text: '⚠︎ ' + m.error })
    }
    return out
  }

  async promptTo(id, text) {
    const v = this.views.get(id); if (!v || !text.trim()) return
    await this.transport.request('POST', `session/${id}/prompt_async`, { query: { directory: v.session.directory }, body: { parts: [{ type: 'text', text }] } })
  }

  async abortView(id) {
    const v = this.views.get(id); if (!v) return
    await this.transport.request('POST', `session/${id}/abort`, { query: { directory: v.session.directory } })
  }

  /// Delete a session on the Mac (what the headset's Delete does): gone from opencode, and from here.
  async deleteSession(id) {
    const s = this.sessions.find((x) => x.id === id)
    await this.transport.request('DELETE', `session/${id}`, { query: s ? { directory: s.directory } : undefined })
    this.views.delete(id)
    await this.loadSessions()
  }

  upsertInto(v, info) {
    if (!info.id) return
    let m = v.messages.get(info.id)
    if (!m) { m = { id: info.id, role: info.role || 'assistant', parts: new Map(), order: [] }; v.messages.set(info.id, m); v.order.push(info.id) }
    m.role = info.role || m.role
    m.createdAt = info.time?.created ?? m.createdAt ?? null   // when it was written: a turn's tag is matched by it (sources.js)
    const error = info.error
    m.error = error && error.name !== 'MessageAbortedError' ? (error.data?.message || error.message || error.name) : null
    // what the session window's header and a reply's actions read: done, stopped, which model, what it cost
    m.completedAt = info.time?.completed ?? null
    m.aborted = error?.name === 'MessageAbortedError'
    m.model = info.modelID ?? info.model?.modelID ?? m.model
    m.provider = info.providerID ?? info.model?.providerID ?? m.provider
    m.tokens = info.tokens ?? m.tokens
    m.cost = info.cost ?? m.cost
  }

  upsertPartInto(v, p) {
    if (!p.id || !p.messageID) return
    let m = v.messages.get(p.messageID)
    if (!m) { this.upsertInto(v, { id: p.messageID, role: 'assistant' }); m = v.messages.get(p.messageID) }
    let part = null
    switch (p.type) {
      case 'text': if (p.ignored) return; part = { kind: 'text', text: p.text || '', synthetic: !!p.synthetic }; break
      case 'reasoning': part = { kind: 'reasoning', text: p.text || '' }; break
      case 'file': part = { kind: 'file', filename: p.filename || '' }; break
      case 'tool': {
        const state = p.state || {}, input = state.input || {}
        const title = [state.title, input.description, input.command, input.filePath, input.pattern, input.url, input.query].find((x) => typeof x === 'string' && x) || ''
        const out = String((state.status === 'error' ? state.error : state.output) || '')
        part = { kind: 'tool', tool: p.tool || 'tool', state: state.status || 'pending', title, time: state.time || null, input, output: out.slice(0, 16000), outputTruncated: out.length > 16000, diff: state.metadata?.diff }
        break
      }
      default: return
    }
    part.id = p.id
    if (!m.parts.has(p.id)) m.order.push(p.id)
    m.parts.set(p.id, part)
  }

  // MARK: sessions

  async loadSessions() {
    const raw = await this.tracked('sessions', 'GET', 'experimental/session', { limit: 200 })
    this.sessions = (raw || []).map((s) => ({
      id: s.id, title: displayTitle(s.title), directory: s.directory || '',   // one line, the placeholder named (core/session-title.js)
      updatedAt: s.time?.updated || 0, parentID: s.parentID || null,
    })).filter((s) => !s.parentID).sort((a, b) => b.updatedAt - a.updatedAt)
    // a rename or opencode's generated title reaches the open windows' views too (never a stale copy)
    for (const [id, v] of this.views) { const s = this.sessions.find((x) => x.id === id); if (s) v.session = s }
    if (this.session) { const s = this.sessions.find((x) => x.id === this.session.id); if (s) this.session = { ...this.session, title: s.title } }
    this.onChange('sessions')
    if (!this.providers.length) this.loadProviders().catch(() => {})
    // who is working right now, for Home's dots (then kept by session.status events)
    this.transport.request('GET', 'session/status').then((map) => {
      for (const [id, st] of Object.entries(map || {})) this.statuses.set(id, st?.type || 'idle')
      this.onChange('sessions')
    }).catch(() => {})
    return this.sessions
  }

  // MARK: a list the Mac refuses (docs/flows/server-errors.yaml)

  /// A sessions or models request: a refusal is kept (the card, the error log, the next try) and still thrown.
  async tracked(subject, method, path, query) {
    try {
      const raw = await this.transport.request(method, path, { query })
      if (this.refused[subject]) { delete this.refused[subject]; this.onChange('problems') }
      return raw
    } catch (e) {
      this.refuse(subject, e, method, path, query)
      throw e
    }
  }

  refuse(subject, e, method, path, query) {
    const status = Number.isInteger(e?.status) ? e.status : 0
    const reason = readReason(status, e?.body)
    const line = recordError({ method, path, queryNames: Object.keys(query || {}), status, reason })
    try { console.warn('server error ' + line) } catch {}   // the log ring (sendlogs/buffer.js) hears it too
    if (!status || status === 503 && e?.stopped) return   // not an answer from opencode: the link's own state says it
    const failures = (this.refused[subject]?.failures || 0) + 1
    this.refused[subject] = { status, reason, failures }
    this.onChange('problems')
    this.scheduleRetry()
  }

  /// The cards: [{subject, title, detail, actions: [{act, label}], retryIn, retrying}] for the Mac named `mac`.
  problems(mac = 'this Mac') {
    return Object.entries(this.refused).map(([subject, r]) => {
      const p = plan({ subject, mac, status: r.status, reason: r.reason, attempt: r.failures })
      return { ...p, retrying: p.retryIn != null && !!this.retryTimer }
    })
  }

  scheduleRetry() {
    if (this.retryTimer) { this.timers.clear(this.retryTimer); this.retryTimer = null }
    const waits = Object.entries(this.refused).map(([subject, r]) => plan({ subject, mac: '', status: r.status, reason: r.reason, attempt: r.failures }).retryIn).filter((n) => n != null)
    if (!waits.length) return
    this.retryTimer = this.timers.set(() => { this.retryTimer = null; this.retryRefused().catch(() => {}) }, Math.min(...waits) * 1000)
  }

  /// Ask again for whatever is refused: a card's Retry, or its wait ending.
  async retryRefused({ reset = false } = {}) {
    if (reset) for (const r of Object.values(this.refused)) r.failures = 0
    const asks = Object.keys(this.refused)
    if (asks.includes('sessions')) await this.loadSessions().catch(() => {})   // reloads the models too when they are empty
    if (asks.includes('models') && this.refused.models) await this.loadProviders().catch(() => {})
    this.onChange('problems')
  }

  // MARK: todos, the diff, the models — the same endpoints OpencodeAdapter.swift reads

  async refreshTodos() {
    const s = this.session; if (!s) return
    const raw = await this.transport.request('GET', `session/${s.id}/todo`, { query: { directory: s.directory } })
    if (this.session !== s) return
    this.todos = (Array.isArray(raw) ? raw : []).map((t) => ({ text: t.content || '', status: t.status || 'pending', done: t.status === 'completed' }))
    this.onChange('todos')
  }

  async refreshDiff() {
    const s = this.session; if (!s) return
    const raw = await this.transport.request('GET', `session/${s.id}/diff`, { query: { directory: s.directory } })
    if (this.session !== s) return
    this.diff = (Array.isArray(raw) ? raw : []).map((f) => ({
      file: f.file || f.path || '?', additions: f.additions ?? 0, deletions: f.deletions ?? 0, rows: simpleDiff(f.before || '', f.after || ''),
    }))
    this.onChange('diff')
  }

  /// A diff refresh, a moment after the last edit rather than on every one.
  diffSoon() {
    clearTimeout(this.diffTimer)
    this.diffTimer = setTimeout(() => this.refreshDiff().catch(() => {}), 1500)
  }

  async loadProviders() {
    const cfg = await this.tracked('models', 'GET', 'config/providers')
    const list = Array.isArray(cfg?.providers) ? cfg.providers : []
    this.providers = list.map((p) => ({
      id: p.id, name: p.name || p.id,
      models: Object.entries(p.models || {}).map(([id, m]) => ({ id, name: m.name || id, free: (m.cost?.input ?? 1) === 0 && (m.cost?.output ?? 1) === 0 })),
    })).filter((p) => p.models.length)
    const d = cfg?.default || {}
    const first = Object.keys(d)[0]
    this.defaultModel = first ? first + '/' + d[first] : null
    this.onChange('providers')
  }

  /// What the agent did, from the tool parts of the open session: newest last.
  activity() {
    const out = []
    for (const id of this.order) {
      const m = this.messages.get(id); if (!m) continue
      for (const pid of m.order) {
        const p = m.parts.get(pid)
        if (!p || p.kind !== 'tool') continue
        const took = p.time?.start && p.time?.end ? p.time.end - p.time.start : null
        out.push({ tool: p.tool, detail: p.title || '', state: p.state, took: took == null ? (p.state === 'completed' ? '—' : '…') : took < 1000 ? took + ' ms' : took < 60000 ? (took / 1000).toFixed(1) + ' s' : Math.round(took / 60000) + 'm ' + Math.round((took % 60000) / 1000) + 's' })
      }
    }
    return out.slice(-40)
  }

  async open(id) {
    const s = this.sessions.find((x) => x.id === id)
    if (!s) return
    this.session = s
    this.messages.clear(); this.order = []
    const raw = await this.transport.request('GET', `session/${id}/message`, { query: { directory: s.directory } })
    for (const item of raw || []) {
      const info = item.info || item
      this.upsertMessage(info)
      for (const p of item.parts || []) this.upsertPart(p)
    }
    this.error = null
    this.onChange('transcript')
    this.todos = []; this.diff = []
    this.refreshTodos().catch(() => {})
    this.refreshDiff().catch(() => {})
  }

  async newSession(directory) {
    const made = await this.transport.request('POST', 'session', { query: { directory }, body: {} })
    await this.loadSessions()
    if (made?.id) await this.open(made.id)
    return made
  }

  async prompt(text) {
    if (!this.session || !text.trim()) return
    const s = this.session
    await this.transport.request('POST', `session/${s.id}/prompt_async`, {
      query: { directory: s.directory }, body: { parts: [{ type: 'text', text }] },
    })
  }

  async abort() {
    if (!this.session) return
    await this.transport.request('POST', `session/${this.session.id}/abort`, { query: { directory: this.session.directory } })
  }

  // MARK: the transcript, as drawn: [{who: 'you'|'cody'|'tool'|'thinking', text}]

  transcript() {
    const out = []
    for (const id of this.order) {
      const m = this.messages.get(id)
      if (!m) continue
      for (const pid of m.order) {
        const p = m.parts.get(pid)
        if (!p) continue
        if (p.kind === 'text' && p.text) out.push({ who: m.role === 'user' ? 'you' : 'cody', text: p.text })
        else if (p.kind === 'tool') out.push({ who: 'tool', text: `${p.tool}${p.title ? ' · ' + p.title : ''}${p.state === 'running' || p.state === 'pending' ? ' …' : ''}` })
        else if (p.kind === 'reasoning' && p.text) out.push({ who: 'thinking', text: p.text })
      }
      if (m.error) out.push({ who: 'tool', text: '⚠︎ ' + m.error })
    }
    return out
  }

  // MARK: events — opencode's /global/event envelope

  async stream({ signal } = {}) {
    this.streaming = true
    try {
      await this.transport.events((envelope) => this.apply(envelope), { signal, onHello: () => this.onChange('stream') })
    } finally { this.streaming = false }
  }

  apply(envelope) {
    const event = envelope.payload || envelope
    for (const fn of this.listeners) { try { fn(event) } catch (e) { console.error(e) } }
    const p = event.properties || {}
    const info = p.info
    const type = event.type || ''
    const sessionID = p.sessionID || info?.sessionID || (type.startsWith('session.') ? info?.id : null)
    // Cody's own, from the Mac's peer page beside opencode's events: where each prompt came from, per session (a hash
    // and a time each — the transcript tags your turns with them, windows/session/panes/transcript/sources.js)
    if (type === 'cody.sources') {
      this.sources = p.sessions && typeof p.sessions === 'object' ? p.sessions : {}
      for (const [id, v] of this.views) { v.sources = this.sources[id] || []; this.onChange('transcript', id) }
      return
    }
    switch (type) {
      case 'session.created': case 'session.updated': case 'session.deleted':
        this.loadSessions().catch(() => {})
        return
      // the Mac app holds the room with the workspace stopped (docs/features/mac-room.md): opencode is back behind it
      case 'cody.workspace':
        if (p.state === 'running') this.loadSessions().catch(() => {})   // the models too, when it had none
        return
      default: break
    }
    if (type === 'session.status' && sessionID) { this.statuses.set(sessionID, p.status?.type || 'idle'); this.onChange('sessions') }
    if (type === 'session.idle' && sessionID) { this.statuses.set(sessionID, 'idle'); this.onChange('sessions') }
    // every open session window gets its own events
    const v = sessionID ? this.views.get(sessionID) : null
    if (v) {
      switch (type) {
        case 'message.updated': this.upsertInto(v, info || {}); break
        case 'message.removed': v.messages.delete(p.messageID); v.order = v.order.filter((x) => x !== p.messageID); break
        case 'message.part.updated': this.upsertPartInto(v, p.part || {}); break
        case 'message.part.delta': { const m = v.messages.get(p.messageID); const part = m?.parts.get(p.partID); if (part && (p.field || 'text') === 'text') part.text = (part.text || '') + (p.delta || ''); break }
        case 'message.part.removed': { const m = v.messages.get(p.messageID); if (m) { m.parts.delete(p.partID); m.order = m.order.filter((x) => x !== p.partID) } break }
        case 'session.status': v.status = p.status?.type === 'busy' ? 'busy' : p.status?.type === 'retry' ? 'retry' : 'idle'; v.retryMessage = p.status?.message; break
        case 'session.idle': v.status = 'idle'; this.refreshViewTodos(sessionID).catch(() => {}); break
        case 'todo.updated': v.todos = (p.todos || []).map((t) => ({ text: t.content || '', status: t.status || 'pending', done: t.status === 'completed' })); break
        case 'session.error': if (p.error?.name !== 'MessageAbortedError') v.error = p.error?.data?.message || p.error?.message || p.error?.name || 'error'; break
        default: break
      }
      this.onChange('transcript', sessionID)
    }
    if (!this.session || sessionID !== this.session.id) return
    switch (type) {
      case 'message.updated': this.upsertMessage(info || {}); break
      case 'message.removed': this.messages.delete(p.messageID); this.order = this.order.filter((x) => x !== p.messageID); break
      case 'message.part.updated': this.upsertPart(p.part || {}); break
      case 'message.part.delta': {
        const m = this.messages.get(p.messageID); const part = m?.parts.get(p.partID)
        if (part && (p.field || 'text') === 'text') part.text = (part.text || '') + (p.delta || '')
        break
      }
      case 'message.part.removed': { const m = this.messages.get(p.messageID); if (m) { m.parts.delete(p.partID); m.order = m.order.filter((x) => x !== p.partID) } break }
      case 'session.status': this.status = p.status?.type === 'busy' ? 'busy' : p.status?.type === 'retry' ? 'retry' : 'idle'; break
      case 'session.idle': this.status = 'idle'; this.refreshTodos().catch(() => {}); this.diffSoon(); break
      case 'todo.updated': this.todos = (p.todos || []).map((t) => ({ text: t.content || '', status: t.status || 'pending', done: t.status === 'completed' })); break
      case 'session.error': if (p.error?.name !== 'MessageAbortedError') this.error = p.error?.data?.message || p.error?.message || p.error?.name || 'error'; break
      default: return
    }
    this.onChange('transcript')
  }

  upsertMessage(info) {
    if (!info.id) return
    let m = this.messages.get(info.id)
    if (!m) { m = { id: info.id, role: info.role || 'assistant', parts: new Map(), order: [] }; this.messages.set(info.id, m); this.order.push(info.id) }
    m.role = info.role || m.role
    const error = info.error
    m.error = error && error.name !== 'MessageAbortedError' ? (error.data?.message || error.message || error.name) : null
  }

  upsertPart(p) {
    if (!p.id || !p.messageID) return
    let m = this.messages.get(p.messageID)
    if (!m) { this.upsertMessage({ id: p.messageID, role: 'assistant' }); m = this.messages.get(p.messageID) }
    let part = null
    switch (p.type) {
      case 'text': if (p.ignored) return; part = { kind: 'text', text: p.text || '' }; break
      case 'reasoning': part = { kind: 'reasoning', text: p.text || '' }; break
      case 'tool': {
        const state = p.state || {}, input = state.input || {}
        const title = [state.title, input.description, input.command, input.filePath, input.pattern, input.url, input.query].find((x) => typeof x === 'string' && x) || ''
        part = { kind: 'tool', tool: p.tool || 'tool', state: state.status || 'pending', title, time: state.time || null }
        if (state.status === 'completed' && ['edit', 'write', 'bash', 'multiedit', 'patch'].includes(p.tool)) this.diffSoon()
        break
      }
      default: return
    }
    part.id = p.id
    if (!m.parts.has(p.id)) m.order.push(p.id)
    m.parts.set(p.id, part)
  }
}

/// A file's before and after as rows the Changes pane draws: the lines that went, the lines that came, the
/// unchanged ones between them — by membership, not by alignment, which is enough to read what an edit did.
export function simpleDiff(before, after, limit = 60) {
  const a = before.split('\n'), b = after.split('\n')
  const inA = new Set(a), inB = new Set(b)
  const rows = []
  let i = 0, j = 0
  while ((i < a.length || j < b.length) && rows.length < limit) {
    if (i < a.length && !inB.has(a[i])) { rows.push(['-', String(i + 1), a[i]]); i++; continue }
    if (j < b.length && !inA.has(b[j])) { rows.push(['+', String(j + 1), b[j]]); j++; continue }
    if (i < a.length && j < b.length) { rows.push([' ', String(j + 1), b[j]]); i++; j++; continue }
    if (i < a.length) { rows.push(['-', String(i + 1), a[i]]); i++ } else { rows.push(['+', String(j + 1), b[j]]); j++ }
  }
  return rows
}
